/**
 * n8n Node Schema Package
 *
 * Provides efficient search and lookup for n8n node schemas.
 * Uses a two-tier storage approach:
 * - Index (~170KB): Lightweight entries for fast search
 * - Individual schemas (~8KB avg): Full details loaded on demand
 */
import { nodeIndex } from './node-index.js';
// Re-export nodeIndex for direct access
export { nodeIndex };
// Pre-built search index for faster lookups
const nodesByName = new Map();
for (const entry of nodeIndex) {
    nodesByName.set(entry.name, entry);
}
// Schema loader function - can be overridden for different environments
let schemaLoader = () => null;
/**
 * Set a custom schema loader function.
 * This allows different environments (Node.js, Workers) to load schemas differently.
 *
 * @param loader Function that loads a schema by node type
 */
export function setSchemaLoader(loader) {
    schemaLoader = loader;
}
// Cache for loaded schemas
const schemaCache = new Map();
/**
 * Check if a query mentions AI agents (used to decide if Tool variants should be included).
 */
function queryMentionsAgent(query) {
    return /\b(agent|ai\s*agent|tool\s*variant|langchain\s*tool)\b/i.test(query);
}
/**
 * Search n8n nodes by keyword.
 * Searches display name, description, and keywords.
 *
 * By default, Tool variant nodes (e.g., slackTool, httpRequestTool) are excluded
 * because they only work when connected to AI Agent nodes. They are included
 * when the query mentions "agent" or when includeToolVariants is set to true.
 *
 * @param query Search query string
 * @param options Search options
 * @returns Matching node index entries
 */
export function searchNodes(query, options = {}) {
    const limit = Math.min(options.limit ?? 10, 25);
    const queryLower = query.toLowerCase().trim();
    if (!queryLower) {
        return [];
    }
    // Determine whether to include Tool variants
    // Default: include only if query mentions "agent" or similar
    const includeToolVariants = options.includeToolVariants ?? queryMentionsAgent(query);
    // Split query into terms for multi-word search
    const terms = queryLower.split(/\s+/).filter((t) => t.length > 0);
    // Score each node based on match quality
    const scored = [];
    for (const entry of nodeIndex) {
        // Filter by group if specified
        if (options.group && entry.group !== options.group) {
            continue;
        }
        // Filter out Tool variants unless explicitly requested or query mentions agents
        if (entry.isToolVariant && !includeToolVariants) {
            continue;
        }
        let score = 0;
        const displayLower = entry.displayName.toLowerCase();
        const descLower = entry.description.toLowerCase();
        for (const term of terms) {
            // Exact display name match (highest priority)
            if (displayLower === term) {
                score += 100;
            }
            // Display name starts with term
            else if (displayLower.startsWith(term)) {
                score += 50;
            }
            // Display name contains term
            else if (displayLower.includes(term)) {
                score += 30;
            }
            // Keyword match
            else if (entry.keywords.some((k) => k.includes(term))) {
                score += 20;
            }
            // Description contains term
            else if (descLower.includes(term)) {
                score += 10;
            }
        }
        if (score > 0) {
            scored.push({ entry, score });
        }
    }
    // Sort by score descending
    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, limit).map((s) => s.entry);
}
/**
 * Get the full schema for a specific node.
 * Uses the configured schema loader to fetch the schema.
 *
 * @param nodeType Full node type, e.g., "n8n-nodes-base.slack"
 * @returns Essential node schema or null if not found
 */
export function getNodeSchema(nodeType) {
    // Check cache first
    const cached = schemaCache.get(nodeType);
    if (cached) {
        return cached;
    }
    // Verify node exists in index
    if (!nodesByName.has(nodeType)) {
        return null;
    }
    // Load using configured loader
    const schema = schemaLoader(nodeType);
    if (schema) {
        schemaCache.set(nodeType, schema);
    }
    return schema;
}
/**
 * Get all node types available in the index.
 *
 * @returns Array of all node type names
 */
export function getAllNodeTypes() {
    return nodeIndex.map((entry) => entry.name);
}
/**
 * Get the total count of nodes in the index.
 */
export function getNodeCount() {
    return nodeIndex.length;
}
/**
 * Check if a node type exists.
 */
export function hasNode(nodeType) {
    return nodesByName.has(nodeType);
}
/**
 * Get a node index entry by name (without full schema).
 */
export function getNodeEntry(nodeType) {
    return nodesByName.get(nodeType) ?? null;
}
/**
 * Filter a node schema to only include properties relevant to a specific resource/operation combo.
 * Properties are removed when their displayOptions clearly exclude the given resource or operation.
 * Conservative: keeps properties with ambiguous conditions (referencing fields other than resource/operation).
 *
 * Returns the original schema unchanged if no resource/operation is provided.
 */
export function filterSchemaByOperation(schema, resource, operation) {
    if (!resource && !operation)
        return schema;
    const filteredProperties = schema.properties.filter((prop) => {
        // Always keep resource, operation, and authentication properties
        if (prop.name === 'resource' || prop.name === 'operation' || prop.name === 'authentication') {
            return true;
        }
        // No displayOptions → always visible → keep
        if (!prop.displayOptions)
            return true;
        const { show, hide } = prop.displayOptions;
        // Check show conditions
        if (show) {
            if (resource && show.resource) {
                if (!show.resource.includes(resource))
                    return false;
            }
            if (operation && show.operation) {
                if (!show.operation.includes(operation))
                    return false;
            }
        }
        // Check hide conditions
        if (hide) {
            if (resource && hide.resource) {
                if (hide.resource.includes(resource))
                    return false;
            }
            if (operation && hide.operation) {
                if (hide.operation.includes(operation))
                    return false;
            }
        }
        return true;
    });
    return { ...schema, properties: filteredProperties };
}
/**
 * Clear the schema cache.
 */
export function clearSchemaCache() {
    schemaCache.clear();
}
//# sourceMappingURL=index.js.map